import { useState, useEffect } from "react";
import { useMeetings } from "./useMeetings";
import styles from "./CalendarPage.module.css";
import { Header } from "../Shared/Header/Header";
import ReactModal from "react-modal";
import Calendar from "./Calendar";
import CalendarEventItem from "./CalendarEventItem";
import AddMeetingModal from "./AddMeetingModal";
import AddItemButton from "ManagementTeam/Shared/AddItemButton";
import CalendarIcon from "./Calendar_Icon.svg";
import { ReactComponent as DeleteIcon } from "../assets/Delete_Icon.svg";
import Button from "shared/Button";
import { useLocation } from "react-router-dom";
import Auth from "shared/@auth";
import { useQueryClient, useIsFetching } from "react-query";
import Loader from "shared/Loader";

export function CalendarPage({ meetingType }) {
  const { hasPermission } = Auth.useContainer();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [meetingInfo, setMeetingInfo] = useState(null); // for editing a meeting in the modal

  const {
    meetings,
    meetingsEC,
    meetingPost,
    meetingPut,
    meetingDelete,
    res,
    statusMT,
    statusEC,
    errorMT,
    errorEC,
  } = useMeetings({ enableExtras: true });

  const [meetingsState, setMeetingsState] = useState([]);
  const [filteredMeetingsState, setFilteredMeetingsState] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [currentMonthYear, setCurrentMonthYear] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const urlLocation = useLocation();
  const queryClient = useQueryClient();
  const fetchingMeetings = useIsFetching(["meetings"]);
  const fetchingMeetingsEC = useIsFetching(["meetingsEC"]);

  useEffect(() => {
    // so we wait to update the new meetings state id until we get the response back which gives us the actual ID
    var array = meetingsState;
    for (var i = 0; i < array.length; i++) {
      if (array[i].id == "not set") {
        array[i].id = res._id;
        break;
      }
    }
    setMeetingsState(array);
    setRefresh(true);
  }, [res]);

  useEffect(() => {
    setMeetingsState(meetingType == "MT" ? meetings : meetingsEC);
  }, [meetingType, meetings, meetingsEC]);

  // Determine loading/error states using React Query status for parity with Dashboard
  const currentStatus = meetingType === "MT" ? statusMT : statusEC;
  const currentError = meetingType === "MT" ? errorMT : errorEC;
  const isLoading = currentStatus === "loading";
  const isError = currentStatus === "error";

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await Promise.all([
        queryClient.invalidateQueries("meetings", {
          refetchActive: true,
          refetchInactive: true,
        }),
        queryClient.invalidateQueries("meetingsEC", {
          refetchActive: true,
          refetchInactive: true,
        }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  function onMeetingSave(meeting) {
    if (meeting.id) {
      // editing a meeting
      var array = meetingsState;
      for (var i = 0; i < array.length; i++) {
        if (array[i].id == meeting.id) {
          array[i] = resJsonToReqJson(meeting);
          break;
        }
      }
      setMeetingsState(array);

      setRefresh(true); // for the calendar, I guess it doesn't detect a change in its props if its just a change in a property of an element of a prop, so i use this
      meetingPut.mutate(meeting);
      closeModal();
    } else {
      // adding a new meeting
      meetingPost.mutate(meeting);
      meeting.id = "not set";
      var array = meetingsState;
      array.push(resJsonToReqJson(meeting));
      setMeetingsState(array);
      closeModal();
    }
  }

  function deleteMeeting(deleteMeetingId) {
    var array = meetingsState;
    for (var i = 0; i < array.length; i++) {
      if (array[i].id === deleteMeetingId) {
        array.splice(i, 1);
        setMeetingsState(array);
        setRefresh(true);
        break;
      }
    }
    meetingDelete.mutate({ meetingId: deleteMeetingId });
    setMeetingInfo(null);
    setDeleteModelOpen(false);
  }

  function clickMeeting(meeting) {
    setMeetingInfo(meeting);
    setModalOpen(true);
  }

  function closeModal() {
    setMeetingInfo(null);
    setModalOpen(false);
  }

  function resJsonToReqJson(meeting) {
    var jsonObject = {};

    jsonObject.meetingType = meeting.meetingType;
    jsonObject.deliveryType = meeting.deliveryType;

    jsonObject.dates = [];
    for (var i = 0; i < meeting.dates.length; i++) {
      jsonObject.dates.push({
        startsOn:
          meeting.dates[i].startDate +
          "T" +
          meeting.dates[i].startTime +
          ".000Z",
        endsOn:
          meeting.dates[i].startDate + "T" + meeting.dates[i].endTime + ".000Z",
        timeZone: meeting.dates[i].timeZone,
      });
    }

    if (meeting.deliveryType.includes("In-person")) {
      jsonObject.inPerson = {
        location: meeting.location,
        state: meeting.state,
        city: meeting.city,
      };
    }

    if (meeting.deliveryType.includes("Virtual")) {
      jsonObject.virtual = {
        zoomLink: meeting.zoomLink,
        meetingId: meeting.meetingId,
        meetingPasscode: meeting.meetingPasscode,
      };
    }

    jsonObject.isDraft = meeting.isDraft;
    jsonObject.id = meeting.id;

    return jsonObject;
  }

  function changeCurrentMonthYear(month, year) {
    setCurrentMonthYear(`${month < 10 ? `0${month}` : month}-${year}`);
  }
  return (
    <>
      <div className={styles.container}>
        <ReactModal
          isOpen={deleteModelOpen}
          className={styles.deleteModal}
          overlayClassName={styles.overlay}
        >
          <div className={styles.deleteModalHeader}>
            <DeleteIcon />
            <p>
              Are you sure you want to delete this event and any items
              associated with it?
            </p>
          </div>
          <div className={styles.deleteModalButtons}>
            <Button
              className={styles.cancelButton}
              children="Cancel"
              onClick={() => {
                setMeetingInfo(null);
                setDeleteModelOpen(false);
              }}
            />
            <Button
              className={styles.deleteButton}
              children="Delete"
              onClick={() => {
                deleteMeeting(meetingInfo.id);
              }}
              purpose="delete"
            />
          </div>
        </ReactModal>
        <AddMeetingModal
          isOpen={modalOpen}
          onSave={onMeetingSave}
          onSaveDraft={onMeetingSave}
          onClose={closeModal}
          onDelete={() => {
            setModalOpen(false);
            setDeleteModelOpen(true);
          }}
          meetingInfo={meetingInfo}
          meetingType={
            urlLocation.pathname.includes("executive-committee")
              ? "Executive Committee"
              : "Management Team"
          }
          currentMonthYear={currentMonthYear}
        />
        <div className={styles.header}>
          <Header
            title={"Calendar"}
            icon={CalendarIcon}
            onRefresh={handleRefresh}
            isLoading={isRefreshing}
            children={<></>}
          />
        </div>
        {isLoading ? (
          <div className={styles.loading}>
            <Loader />
          </div>
        ) : isError ? (
          <div className={styles.loading}>
            Error, please contact support or try again later.
          </div>
        ) : (
          <div className={styles.centerContainer}>
            <div className={styles.lowerContainer}>
              <div
                style={{
                  visibility: hasPermission("management-team.admins")
                    ? "visible"
                    : "hidden",
                }}
                className={styles.addItemButtonContainer}
              >
                <AddItemButton onClick={() => setModalOpen(true)} />
                <div className={styles.addItemButtonText}>ADD MEETING</div>
              </div>

              <div className={styles.calendarGrid}>
                <div className={styles.calendarContainer}>
                  <Calendar
                    refresh={refresh}
                    setRefresh={setRefresh}
                    events={meetingsState}
                    onClickMeeting={clickMeeting}
                    changeCurrentMonthYear={changeCurrentMonthYear}
                  />
                </div>
                <div className={styles.calendarEventItemsContainer}>
                  {meetingsState
                    .sort((a, b) => {
                      return (
                        new Date(a.dates[0].startsOn) -
                        new Date(b.dates[0].startsOn)
                      );
                    })
                    .map((item, index) => {
                      var currentItemMonth =
                        new Date(item.dates[0].startsOn).getUTCMonth() + 1;
                      currentItemMonth = `${
                        currentItemMonth < 10
                          ? `0${currentItemMonth}`
                          : currentItemMonth
                      }`;
                      if (
                        currentItemMonth ===
                          currentMonthYear.substring(
                            0,
                            currentMonthYear.indexOf("-")
                          ) &&
                        new Date(item.dates[0].startsOn)
                          .getUTCFullYear()
                          .toString() ===
                          currentMonthYear.substring(
                            currentMonthYear.indexOf("-") + 1
                          )
                      ) {
                        return (
                          <CalendarEventItem
                            event={item}
                            onClick={clickMeeting}
                            onPublish={onMeetingSave}
                            key={index}
                          />
                        );
                      }
                    })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
